import { useMutation, useQuery } from '@tanstack/react-query'
import { FiEdit, FiExternalLink, FiPlus, } from 'react-icons/fi'
import { Formik, Form, Field } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import Card from '../../../../components/Card'
import { api, authHeaders } from '../../../../services/api'
import Spinner from '../../../../components/Spinner'
import { Button, Select, Textbox } from '../../../../components/Form'
import Modal from '../../../../components/Modal'
import Table from '../../../../components/Table'
import maskUtils from '../../../../util/masks'
import formUtils from '../../../../util/form'
import { getAge } from '../../../../util/date'
import Yup from '../../../../services/yup'
import { GlobalContext } from '../../../../contexts/GlobalContext'
import { formatAddress } from '../../../../util/string'

const validation = Yup.object({
    nome: Yup.string(),
    data_nascimento: Yup.string().date().required('Especifique a data de nascimento.'),
    whatsapp: Yup.string().mobile(),
    email: Yup.string().email('E-mail incorreto').required('Informe um e-mail.'),
    sexo: Yup.string().required('Selecione uma opção')
})

const baseInitialValues = {
    nome: '',
    data_nascimento: '',
    whatsapp: '',
    email: '',
    sexo: null,
    cpf: '',
    plano: null
}

export default function () {
    const history = useHistory()

    const { user } = useContext(GlobalContext)

    const [beneficiarios, setBeneficiarios] = useState(null)
    const [isLoadingBeneficiarios, setIsLoadingBeneficiarios] = useState(false)
    const [showModalCadastro, setShowModalCadastro] = useState(false)
    const [beneficiarioEmEdicao, setBeneficiarioEmEdicao] = useState(null)
    const [parceiroSelecionado, setParceiroSelecionado] = useState(null)
    const [initialValues, setInitialValues] = useState(baseInitialValues)

    const { data: parceiros } = useQuery({
        queryKey: ['get-parceiros'],
        queryFn: async () => {
            const response = await api.get('parceiro', {
                params: {
                    pessoa_id: user.pessoa.id
                },
                ...authHeaders()
            })

            return response.map(item => ({
                id: item.id,
                nome: item.pessoa.nome,
                cnpj: item.pessoa.documentos.find(d => d.tipo_documento_id === 14).identificador,
                endereco: formatAddress(item.pessoa.endereco),
                email: item.pessoa.contato.find(c => c.tipo_contato_id === 6).contato,
                fone: item.pessoa.contato.find(c => c.tipo_contato_id === 4).contato
            }))
        },
        enabled: !!authHeaders()
    })

    const { data: planos } = useQuery({
        queryKey: ['obter-planos-disponiveis'],
        queryFn: async () => {
            const response = await api.get('plano', {
                params: {
                    modulo_id: 1
                }
            })

            return response
        },
        refetchOnWindowFocus: false
    })

    async function obterBeneficiarios() {
        try {
            setIsLoadingBeneficiarios(true)

            const response = await api.get(`parceiro/${parceiroSelecionado.value}`, authHeaders())

            const clientes = response.clientes.map(cliente => ({
                id: cliente.id,
                nome: cliente.nome,
                whatsapp: cliente.contato.find(c => c.tipo_contato_id === 4)?.contato || '',
                email: cliente.contato.find(c => c.tipo_contato_id === 6)?.contato || '',
                cpf: cliente.documentos.find(c => c.tipo_documento_id === 2)?.identificador || '',
                data_nascimento: cliente.data_nascimento,
                sexo: cliente.sexo
            }))
            
            setBeneficiarios(clientes.map(item => ({
                id: item.id,
                nome: item.nome,
                sexo_original: item.sexo,
                sexo: item.sexo ? (item.sexo === 'M' ? 'Masculino' : 'Feminino') : '-',
                email: item.email,
                whatsapp: item.whatsapp,
                cpf: item.cpf,
                data_nascimento_e_idade: !!item.data_nascimento ? `${format(new Date(item.data_nascimento), 'dd/MM/yyyy')} (${getAge(item.data_nascimento)} anos)` : '-',
                data_nascimento: !!item.data_nascimento ? `${format(new Date(item.data_nascimento), 'dd/MM/yyyy')}` : 'não informado',
            })))
        } catch (e) {
            toast.error(e.msg)
        } finally {
            setIsLoadingBeneficiarios(false)
        }
    }

    function handleAcessarProntuario(item) {
        history.push({
            pathname: '/prontuario/membro',
            state: {
                membro_id: item.id
            }
        })
    }

    function handleSubmitBeneficiario(data) {
        if (showModalCadastro) {
            addBeneficiario(data)
        } else {
            updateBeneficiario(data)
        }
    }

    function prepareEdit(data) {
        setBeneficiarioEmEdicao(data)

        setInitialValues({
            nome: data.nome,
            data_nascimento: data.data_nascimento,
            whatsapp: data.whatsapp,
            email: data.email,
            sexo: { label: data.sexo, value: data.sexo_original },
            cpf: data.cpf,
            plano: null
        })
    }

    const { mutate: addBeneficiario, isPending: isCreatingBeneficiario } = useMutation({
        mutationFn: async data => {
            data = formUtils.extractFormValues(data)

            data.parceiro_id = String(parceiroSelecionado.value)

            await api.post(`parceiro/${parceiroSelecionado.value}/beneficiario`, data, authHeaders())

            setShowModalCadastro(false)

            toast.success('Cadastro realizado com sucesso.')
        },
        onError: e => {
            toast.error(e.msg)
        },
        onSuccess() {
            obterBeneficiarios()
        }
    })

    const { mutate: updateBeneficiario, isPending: isUpdatingBeneficiario } = useMutation({
        mutationFn: async data => {
            data = formUtils.extractFormValues(data)

            data.parceiro_id = String(parceiroSelecionado.value)

            await api.put(`parceiro/${parceiroSelecionado.value}/beneficiario/${beneficiarioEmEdicao.id}`, data, authHeaders())
        },
        onError: e => {
            toast.error(e.msg)
        },
        onSuccess() {
            toast.success('Beneficiário atualizado com sucesso.')

            setBeneficiarioEmEdicao(null)
            
            obterBeneficiarios()
        }
    })

    useEffect(() => {
        if (parceiroSelecionado) {
            obterBeneficiarios()
        }
    }, [parceiroSelecionado])

    return (
        <>
            <div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:my-12 min-h-[46vh] leading-loose">
                <Card className="flex flex-col">
                    <h1 className="mx-2 text-slate-800">Gerenciamento de beneficiários</h1>
                    <span className='mx-2'>Gerencie aqui os usuários cadastrados.</span>

                    <div className='flex gap-3 items-end'>
                        <Select
                            withoutForm
                            name="parceiro"
                            label="Selecione a empresa/parceiro para gerenciar os beneficiários"
                            options={parceiros?.map(item => ({
                                label: item.nome,
                                value: item.id
                            }))}
                            onChange={setParceiroSelecionado}
                            value={parceiroSelecionado}
                        />

                        {!!parceiroSelecionado && (
                            <Button onClick={() => { setShowModalCadastro(true) }} className="w-80 mt-4">
                                Cadastrar novo beneficiário
                                <FiPlus size={16} />
                            </Button>
                        )}
                    </div>

                    {isLoadingBeneficiarios ? (
                        <Spinner />
                    ) : !!beneficiarios ? (
                        <div className="mx-2 mt-8">
                            <span className="font-medium text-lg mt-2">Beneficiários</span>

                            <Table
                                headers={[
                                    { name: 'nome', value: 'Nome' },
                                    { name: 'sexo', value: 'Sexo', centered: true },
                                    { name: 'data_nascimento_e_idade', value: 'Data de nascimento', centered: true },
                                    { name: 'email', value: 'Email', centered: true },
                                    { name: 'whatsapp', value: 'WhatsApp', centered: true },
                                ]}
                                data={beneficiarios}
                                actions={[
                                    {
                                        action: prepareEdit,
                                        name: 'Editar',
                                        icon: FiEdit,
                                        iconSize: 16
                                    },
                                    {
                                        action: handleAcessarProntuario,
                                        name: 'Acessar prontuário',
                                        icon: FiExternalLink,
                                        iconSize: 16
                                    }
                                ]}
                                emptyLabel="Nenhum usuário foi incluído."
                            />
                        </div>
                    ) : null}
                </Card>
            </div>

            <Modal
                isOpen={showModalCadastro || !!beneficiarioEmEdicao}
                handleClose={() => {
                    setShowModalCadastro(false)
                    setBeneficiarioEmEdicao(null)
                    setInitialValues(baseInitialValues)
                }}
                title={beneficiarioEmEdicao ? 'Edição de beneficiário' : 'Novo beneficiário'}
            >
                <div className="flex flex-col gap-2">
                    <Formik
                        onSubmit={handleSubmitBeneficiario}
                        initialValues={initialValues}
                        validationSchema={validation}
                        enableReinitialize
                    >
                        {({ setFieldValue }) => {

                            return (
                                <Form className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3">
                                    <Textbox
                                        name="nome"
                                        label="Nome completo"
                                    />

                                    <Textbox
                                        name="cpf"
                                        label="CPF"
                                        mask={maskUtils.cpf}
                                    />

                                    <Select
                                        name="sexo"
                                        label="Selecione o sexo"
                                        onChange={(selected, meta) => {
                                            setFieldValue(meta.name, selected)
                                        }}
                                        options={[
                                            { label: 'Masculino', value: 'M' },
                                            { label: 'Feminino', value: 'F' }
                                        ]}
                                    />

                                    <Textbox
                                        name="data_nascimento"
                                        label="Data de nascimento"
                                        mask={maskUtils.date}
                                    />

                                    <Textbox
                                        name="email"
                                        label="E-mail"
                                        className="lg:col-start-1"
                                    />

                                    <Textbox
                                        name="whatsapp"
                                        label="WhatsApp"
                                        mask={maskUtils.mobile}
                                    />

                                    {showModalCadastro && (
                                        <Select
                                            name="plano"
                                            label="Selecione o plano"
                                            onChange={(selected, meta) => {
                                                setFieldValue(meta.name, selected)
                                            }}
                                            options={planos.map(plano => ({
                                                label: plano.descricao,
                                                value: plano.id
                                            }))}
                                        />
                                    )}

                                    <div className="col-span-full flex justify-end mt-4">
                                        <Button type="submit" loading={isCreatingBeneficiario || isUpdatingBeneficiario}>
                                            {beneficiarioEmEdicao ? 'Atualizar' : 'Cadastrar'}
                                        </Button>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>
                </div>
            </Modal>
        </>
    )
}


